import {EventEmitter, Injectable}    from '@angular/core';
import {ApDynGridStatusColumnConfig} from '../../../ap-dyngrids/config/ap-dyn-grid-status-column-config';
import {
  ApDynGridPropertyColumnConfig,
  FilterType
}                                    from '../../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {ApDynGridGroupColumnConfig}  from '../../../ap-dyngrids/config/ap-dyn-grid-group-column-config';
import {ApDynGridEditColumnConfig}   from '../../../ap-dyngrids/config/ap-dyn-grid-edit-column-config';
import {ApDynGridColumnConfigBase}   from '../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {LanguageStore}               from '../../../stores/translation/language.store';
import {GetRoundNumericPipe}         from '../../../ap-utils';
import {SettingsStore}               from '../../../stores/base-data/settings.store';
import {GetElementService}           from '../../../ap-utils/service/ap-get-element.service';
import {ApTranslationService}        from '../../../ap-utils/service/ap-translation.service';
import {ApElementType}               from '../../../ap-interface/enums/ap-elements-type.enum';
import {GetRoundNumericPipeConfig}   from '../../../ap-utils/pipes/configs/get-round-numeric.pipe.config';

@Injectable({
  providedIn: 'root'
})
export class GridBuilderService {

  constructor(private languageStore: LanguageStore,
              private roundNumericPipe: GetRoundNumericPipe,
              private settingsStore: SettingsStore,
              private translationService: ApTranslationService,
              private elementService: GetElementService) {
  }

  public PlanBooksGrid(options: { text: string, callback: EventEmitter<any> }[]): ApDynGridColumnConfigBase[] {
    return [
      new ApDynGridStatusColumnConfig({
        field: 'Status',
        class: 'StatusClass',
        description: 'StatusDesc',
        filterable: true,
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'ApplicationDate',
        title: 'Nutrients__FertilizationDate',
        tooltip: (dataItem) => dataItem.ApplicationDateTooltip,
        dynCssClass: (dataItem) => dataItem.ApplicationDateItalic ? ' italic' : '',
        hide: true,
        width: 120,
        filterable: true,
        sortIndex: 1,
        sortDesc: true,
        format: this.languageStore.CurrentDateFormat
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'Product',
        title: 'Global__Product',
        width: 170,
        filterable: true,
        tooltip: (dataItem) => dataItem['ProductTooltip']
      }),
      new ApDynGridGroupColumnConfig({
        title: `${this.translationService.translate('Nutrients__Average_Amount_Goods')} / ${this.translationService.translate('Global__Remaining_Need_Two_Short')}`,
        headerStyle: {'text-align': 'center'},
        groupColumns: [
          new ApDynGridPropertyColumnConfig({
            field: 'Amount',
            width: 80,
            cssClass: 'right',
            filterType: FilterType.NUMBER,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            field: 'AmountUnit',
            width: 60
          })
        ]
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'Count',
        title: 'Global__Amount',
        width: 70,
        filterable: true,
        tooltip: (dataItem) => dataItem?.FieldsTooltip,
        cssClass: 'right',
        filterType: FilterType.NUMBER
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'Area',
        title: 'Global__Area_Unit',
        width: 80,
        filterable: true,
        cssClass: 'right',
        filterType: FilterType.NUMBER,
        tooltip: (dataItem) => dataItem['AreaOriginal'],
        pipes: [{
          pipe: this.roundNumericPipe,
          args: [this.settingsStore.FirstSetting.DigitsAfterDecimalPoint]
        }]
      }),
      new ApDynGridGroupColumnConfig({
        title: this.translationService.translate('Global__Sum_Goods'),
        headerStyle: {'text-align': 'center'},
        hide: true,
        groupColumns: [
          new ApDynGridPropertyColumnConfig({
            field: 'Sum',
            hide: true,
            width: 90,
            tooltip: (dataItem) => dataItem['SumTooltip'],
            cssClass: 'right',
            filterType: FilterType.NUMBER,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            field: 'SumUnit',
            width: 60,
            hide: true,
          })
        ]
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'LeadNutrient',
        title: 'AgriConnect__Nutrient',
        width: 90,
        filterable: true
      }),
      new ApDynGridGroupColumnConfig({
        title: 'Global__Average_Symbol_Nutrient_kg_ha',
        groupColumns: [
          new ApDynGridPropertyColumnConfig({
            title: this.elementService.GetElementStringByString(ApElementType.P),
            field: 'P',
            cssClass: 'right',
            filterType: FilterType.NUMBER,
            width: 60,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0, GetRoundNumericPipeConfig.NullAndZeroToEmptyReplaceConfig]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            title: this.elementService.GetElementStringByString(ApElementType.K),
            field: 'K',
            cssClass: 'right',
            filterType: FilterType.NUMBER,
            width: 60,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0, GetRoundNumericPipeConfig.NullAndZeroToEmptyReplaceConfig]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            title: this.elementService.GetElementStringByString(ApElementType.Mg),
            field: 'Mg',
            cssClass: 'right',
            filterType: FilterType.NUMBER,
            width: 60,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0, GetRoundNumericPipeConfig.NullAndZeroToEmptyReplaceConfig]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__NutrientCaO',
            field: 'CaO',
            cssClass: 'right',
            filterType: FilterType.NUMBER,
            width: 60,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0, GetRoundNumericPipeConfig.NullAndZeroToEmptyReplaceConfig]
            }]
          }),
        ]
      }),
      new ApDynGridGroupColumnConfig({
        title: 'Global__Good_ha',
        hide: true,
        groupColumns: [
          new ApDynGridPropertyColumnConfig({
            title: 'Min',
            field: 'Min',
            cssClass: 'right',
            filterType: FilterType.NUMBER,
            width: 70,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Max',
            field: 'Max',
            cssClass: 'right',
            filterType: FilterType.NUMBER,
            width: 70,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0]
            }]
          }),
        ]
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'Note',
        title: 'Global__Note',
        hide: true,
        cssClass: 'left',
        width: 100
      }),
      new ApDynGridEditColumnConfig({
        data: options
      })
    ];
  }
}
